import { useState, useEffect } from "react";
import { Button, Checkbox, Divider, Input, Table, Typography, message } from "antd";
import { Navigate, useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { CalendarOutlined, CloseCircleOutlined } from "@ant-design/icons";
import {
  getFormData,
  getRequestStatus,
  submitSampleRequest,
  getRequestIdFromUrl,
} from "../../api/genericSamplesIntegrationApi";
import { FormData, Address } from "../../api/samplesIntegrationApi";
import {
  getPartnerConfig,
  resolveDrugProducts,
  resolveDrugCopy,
} from "../../config/genericPartners";
import {
  DocUpdateHeader,
  DocUpdateFooter,
  LoadingState,
  ErrorState,
} from "./components/DocUpdateLayout";
import { renderSignatureImage } from "../../utils/methods/renderSignatureImage";
import "./GenericSampleRequestForm.css";

const PREFIX = "gen-sample-request";

interface OrderRow {
  key: string;
  quantity: string;
  product: string;
  ndcSku: string;
  code: string;
  description: string;
}

const formatPhoneNumber = (phone: string): string => {
  const digits = Array.from(phone, (c) => (c >= "0" && c <= "9" ? c : "")).join("");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });

// URL parsing is done with plain string scanning (no regex) to avoid any
// ReDoS surface entirely — every operation below is linear in the input length.
const URL_SCHEMES = ["https://", "http://"];
const TRAILING_PUNCT = ".,);";
const WHITESPACE = " \t\n\r\f\v";

interface TextSegment {
  isUrl: boolean;
  value: string;
}

/** Split text into URL / non-URL segments by scanning for http(s):// schemes up to the next whitespace. */
const splitOnUrls = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let cursor = 0;
  let textStart = 0;
  while (cursor < text.length) {
    const scheme = URL_SCHEMES.find((s) => text.startsWith(s, cursor));
    if (!scheme) {
      cursor++;
      continue;
    }
    if (cursor > textStart) segments.push({ isUrl: false, value: text.slice(textStart, cursor) });
    let end = cursor + scheme.length;
    while (end < text.length && !WHITESPACE.includes(text[end])) end++;
    segments.push({ isUrl: true, value: text.slice(cursor, end) });
    cursor = end;
    textStart = end;
  }
  if (textStart < text.length) segments.push({ isUrl: false, value: text.slice(textStart) });
  return segments;
};

/** Render free text, turning inline http(s) URLs into links (trailing punctuation preserved outside the link). */
const renderWithLinks = (text: string) =>
  splitOnUrls(text).map((segment, i) => {
    if (!segment.isUrl) return <span key={i}>{segment.value}</span>;
    let end = segment.value.length;
    while (end > 0 && TRAILING_PUNCT.includes(segment.value[end - 1])) end--;
    const url = segment.value.slice(0, end);
    const trail = segment.value.slice(end);
    return (
      <span key={i}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
        {trail}
      </span>
    );
  });

const SampleRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { partner = "" } = useParams<{ partner: string }>();
  const partnerConfig = getPartnerConfig(partner);

  const [fullName, setFullName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameMismatch, setNameMismatch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestID, setRequestID] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  useEffect(() => {
    if (!partnerConfig) return;

    const initializeForm = async () => {
      const requestIdFromUrl = getRequestIdFromUrl() || searchParams.get("requestID");

      if (!requestIdFromUrl) {
        setError("Invalid request. No request ID provided.");
        setIsLoading(false);
        return;
      }

      setRequestID(requestIdFromUrl);

      // Always re-check with the backend on every mount. The prior
      // location.state short-circuit let a browser Back into a stale
      // history entry render the form without ever asking the server if
      // the OTP was still valid; we now gate every mount on getRequestStatus.
      try {
        const status = await getRequestStatus(partner, requestIdFromUrl);
        if (status.otpStatus !== "verified") {
          navigate(`/${partner}/verify-phone?requestID=${requestIdFromUrl}`);
          return;
        }
        if (status.isClosed) {
          navigate(`/${partner}/form-unavailable`, {
            state: { reason: status.formStatus === "submitted" ? "submitted" : "closed" },
          });
          return;
        }
        const response = await getFormData(partner, requestIdFromUrl);
        setFormData(response.formData);
        // Preserve the address the user picked on AddressSelection when
        // available; otherwise default to the first address on the record.
        const preselected = location.state?.selectedAddress as Address | undefined;
        if (preselected) {
          setSelectedAddress(preselected);
        } else if (response.formData.addresses?.length > 0) {
          setSelectedAddress(response.formData.addresses[0]);
        }
      } catch (err: any) {
        console.error(`[generic][${partner}][SampleRequestForm] init failed:`, err);
        setError(err.message || "Failed to load form data");
        if (err.message?.includes("not found") || err.message?.includes("expired")) {
          navigate(`/${partner}/form-unavailable`, { state: { reason: "expired" } });
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [location.state, navigate, searchParams, partner, partnerConfig]);

  useEffect(() => {
    if (!showTimestamp) return;
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, [showTimestamp]);

  if (!partnerConfig) return <Navigate to="/not-found" replace />;
  if (isLoading) return <LoadingState prefix={PREFIX} message="Loading form data..." />;
  if (error) return <ErrorState prefix={PREFIX} error={error} />;

  const drugName = formData?.decision?.drugName;
  const copy = resolveDrugCopy(partner, drugName)!;

  const generalInfo = {
    hcpName: formData?.hcp
      ? `${formData.hcp.firstName} ${formData.hcp.lastName}`
      : location.state?.generalInfo?.hcpName || "Loading...",
    designation: formData?.hcp?.designation || location.state?.generalInfo?.designation || "",
    specialty: formData?.hcp?.specialty || location.state?.generalInfo?.specialty || "",
    stateLicenseNumber:
      selectedAddress?.stateLicense?.licNumber || location.state?.generalInfo?.stateLicenseNumber || "",
    phoneNumber: formData?.hcp?.phoneNumber || location.state?.generalInfo?.phoneNumber || "",
    shippingStreet: selectedAddress
      ? `${selectedAddress.street}${selectedAddress.suite ? `, ${selectedAddress.suite}` : ""}`
      : location.state?.generalInfo?.shippingStreet || "",
    shippingCity: selectedAddress
      ? `${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zipCode}`
      : location.state?.generalInfo?.shippingCity || "",
  };

  const requestNumber = formData?.requestInfo?.requestNumber || location.state?.requestNumber || "";
  const requestDate = formData?.requestInfo?.requestDate || location.state?.requestDate || "";

  const resolvedProducts = formData?.decision
    ? resolveDrugProducts(partner, drugName, formData.decision.products)
    : location.state?.orderItems || [];

  const orderItems: OrderRow[] = resolvedProducts.map((p: any, i: number) => ({
    key: String(i + 1),
    quantity: p.totalQuantity || "",
    product: p.productGroup || "",
    ndcSku: p.ndc || "",
    code: p.code || "",
    description: p.productDescription || "",
  }));

  const columns = [
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (value: string) => <span className="product-cell">{value}</span>,
    },
    { title: "NDC/SKU Number", dataIndex: "ndcSku", key: "ndcSku" },
    { title: "Code", dataIndex: "code", key: "code" },
    { title: "Description", dataIndex: "description", key: "description" },
  ];

  const expectedFullName = formData?.hcp ? `${formData.hcp.firstName} ${formData.hcp.lastName}`.trim() : "";

  const handleNameChange = (value: string) => {
    setFullName(value);
    setShowTimestamp(!!value.trim());
    setNameMismatch(
      !!value.trim() && !!expectedFullName && value.trim().toLowerCase() !== expectedFullName.toLowerCase()
    );
  };

  const handleSubmit = async () => {
    if (!requestID) return message.error("Invalid request.");
    if (!agreedToTerms) return message.error("Please agree to the terms before submitting.");
    if (!fullName.trim()) return message.error("Please enter your full name.");
    if (nameMismatch) return message.error("Prescriber name must match the Sample Request Form.");
    if (!selectedAddress) return message.error("No shipping address selected.");

    setIsSubmitting(true);
    try {
      const signedName = fullName.trim();
      const signatureImage = await renderSignatureImage(signedName);
      if (!signatureImage) {
        throw new Error("Could not render signature image. Please try again.");
      }
      const result = await submitSampleRequest(partner, requestID, selectedAddress, {
        fullName: signedName,
        signedAt: new Date().toISOString(),
        agreedToTerms,
        signatureImage,
      });
      message.success("Request submitted successfully!");
      navigate(`/${partner}/form-unavailable`, {
        state: { reason: "success", submittedAt: result.submittedAt, requestNumber },
      });
    } catch (err: any) {
      message.error(err.message || "Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${PREFIX}-container`}>
      <DocUpdateHeader prefix={PREFIX} />

      <main className={`${PREFIX}-main`}>
        <Typography.Title className={`${PREFIX}-title`}>Rx Sample Request Form</Typography.Title>
        <Typography.Text className={`${PREFIX}-number`}>Request #: {requestNumber || "—"}</Typography.Text>
        <Typography.Text className={`${PREFIX}-date`}>Request Date: {requestDate || "—"}</Typography.Text>

        <section className={`${PREFIX}-section`}>
          <Typography.Text className={`${PREFIX}-section-title`}>General Information</Typography.Text>
          <div className={`${PREFIX}-info-grid`}>
            <div className="info-card">
              <Typography.Text className="info-card-label">Prescriber Information</Typography.Text>
              <Typography.Text className="info-card-value">{generalInfo.hcpName}</Typography.Text>
              {generalInfo.designation && (
                <Typography.Text className="info-card-value">
                  Professional Designation: {generalInfo.designation}
                </Typography.Text>
              )}
              {generalInfo.specialty && (
                <Typography.Text className="info-card-value">Specialty: {generalInfo.specialty}</Typography.Text>
              )}
              <Typography.Text className="info-card-value">
                State License Number: {generalInfo.stateLicenseNumber}
              </Typography.Text>
              {generalInfo.phoneNumber && (
                <Typography.Text className="info-card-value">
                  Phone Number: {formatPhoneNumber(generalInfo.phoneNumber)}
                </Typography.Text>
              )}
            </div>
            <div className="info-card">
              <Typography.Text className="info-card-label">Shipping Address</Typography.Text>
              <Typography.Text className="info-card-value">{generalInfo.shippingStreet}</Typography.Text>
              <Typography.Text className="info-card-value">{generalInfo.shippingCity}</Typography.Text>
            </div>
          </div>
        </section>

        <section className={`${PREFIX}-section`}>
          <div className={`${PREFIX}-section-header`}>
            <Typography.Text className={`${PREFIX}-section-title`}>Sample Request Summary</Typography.Text>
          </div>

          {copy.sampleRequestInstructions.length > 0 && (
            <>
              <Typography.Text className="instructions-label">Sample Request Instructions:</Typography.Text>
              <ol className="instructions-list">
                {copy.sampleRequestInstructions.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ol>
            </>
          )}

          <div className={`${PREFIX}-table-wrapper`}>
            <Table columns={columns} dataSource={orderItems} pagination={false} className={`${PREFIX}-table`} />
          </div>
          <div className={`${PREFIX}-disclaimer`}>
            <Typography.Text className="disclaimer-text">{copy.distributorText}</Typography.Text>
          </div>
        </section>

        <section className={`${PREFIX}-section`}>
          <Typography.Text className={`${PREFIX}-section-title`}>
            Electronic Signature & Confirmation
          </Typography.Text>

          <div className="signature-agreement">
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="signature-checkbox"
            >
              <span className="signature-agreement-text">{copy.signatureCertificationText}</span>
            </Checkbox>
          </div>

          {copy.stateLicensureNotice && (
            <Typography.Paragraph className="state-licensure-notice">
              {renderWithLinks(copy.stateLicensureNotice)}
            </Typography.Paragraph>
          )}

          <div className="signature-inputs">
            <div className="signature-name-section">
              <Typography.Text className="signature-input-label">Prescriber Full Name</Typography.Text>
              <Input
                value={fullName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter your full name"
                className="signature-name-input"
                status={nameMismatch ? "error" : undefined}
                suffix={nameMismatch ? <CloseCircleOutlined style={{ color: "#ef4444" }} /> : <span />}
              />
              {nameMismatch && (
                <Typography.Text
                  className="signature-name-error"
                  style={{ color: "#ef4444", fontSize: 13, marginTop: 4, display: "block" }}
                >
                  Prescriber name must match the Sample Request Form.
                </Typography.Text>
              )}
            </div>
            <div className="signature-display-section">
              <Typography.Text className="signature-input-label">Electronic Signature</Typography.Text>
              <div className="signature-display-box">
                {fullName.trim() ? (
                  <span className="signature-text">{fullName}</span>
                ) : (
                  <span className="signature-placeholder">Your signature will appear here</span>
                )}
              </div>
              {showTimestamp && (
                <div className="signature-timestamp">
                  <CalendarOutlined className="timestamp-icon" />
                  <Typography.Text className="timestamp-text">
                    Request Signed on {formatDate(currentTime)}
                  </Typography.Text>
                </div>
              )}
            </div>
          </div>

          <Divider className="signature-divider" />

          <div className="signature-actions">
            <Button
              onClick={() => {
                setFullName("");
                setAgreedToTerms(false);
                setShowTimestamp(false);
                navigate(-1);
              }}
              className="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!agreedToTerms || !fullName.trim() || nameMismatch}
              className="submit-btn"
            >
              Submit Request
            </Button>
          </div>
        </section>
      </main>

      <DocUpdateFooter prefix={PREFIX} />
    </div>
  );
};

export default SampleRequestForm;
